import noop from 'lodash/noop';
import uniqBy from 'lodash/uniqBy';
import {
	type Action,
	INITIAL_BOARDS_FETCHED,
	START_FETCHING_INITIAL_BOARDS,
	INITIAL_BOARDS_FETCH_FAILURE,
	SET_UP_CONTEXT,
	SET_RECENTLY_CLICKED_ITEM,
	SEARCH_CHANGED,
	SEARCH_SUCCESS,
	FETCHED_BOARD_OWNER_ACCOUNT_ID,
	SEARCH_FAILURE,
	LOAD_MORE,
	LOAD_MORE_SUCCESS,
	LOAD_MORE_FAILURE,
} from './actions.tsx';
import type { State } from './types.tsx';

const emptyBoardsList = () => ({
	values: [],
	total: 0,
});

export const initialState: State = {
	currentProjectKey: undefined,
	initialBoards: {
		inLocation: emptyBoardsList(),
		recent: [],
		wereFetched: false,
		isInProgress: false,
		hasError: false,
	},
	currentBoard: undefined,
	searchResults: {
		inLocation: emptyBoardsList(),
		notInLocation: emptyBoardsList(),
	},
	boardOwnerAccountId: undefined,
	isSearchInProgress: false,
	isLoadMoreInProgress: false,
	shouldRetryLoadMore: false,
	hasSearchError: false,
	searchValue: '',
	viewAllUrl: '',
	baseUrl: '',
	recentlyClickedItem: undefined,
	// @ts-expect-error - TS2322 - Type '(...args: any[]) => void' is not assignable to type 'OnBoardClick'.
	onBoardClick: noop,
	onNavigateUrl: noop,
};

const patchInitialBoards = (
	state: State,
	initialBoardsPatch: Partial<State['initialBoards']>,
): State => ({
	...state,
	initialBoards: {
		...state.initialBoards,
		...initialBoardsPatch,
	},
});

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (state: State = initialState, action: Action): State => {
	switch (action.type) {
		case SET_UP_CONTEXT: {
			const { boardsInLocation, ...rest } = action.payload;
			return {
				...state,
				...rest,
				initialBoards: {
					...state.initialBoards,
					...(boardsInLocation ? { inLocation: boardsInLocation } : Object.freeze({})),
				},
			};
		}

		case START_FETCHING_INITIAL_BOARDS: {
			return patchInitialBoards(state, {
				isInProgress: true,
				hasError: false,
			});
		}

		case INITIAL_BOARDS_FETCHED: {
			const { recentBoards, boardsInLocation } = action.payload;

			return patchInitialBoards(state, {
				recent: recentBoards,
				hasError: false,
				wereFetched: true,
				isInProgress: false,
				...(boardsInLocation ? { inLocation: boardsInLocation } : {}),
			});
		}

		case INITIAL_BOARDS_FETCH_FAILURE: {
			return patchInitialBoards(state, {
				isInProgress: false,
				hasError: true,
			});
		}

		case SET_RECENTLY_CLICKED_ITEM:
			return {
				...state,
				recentlyClickedItem: action.payload,
			};

		case SEARCH_CHANGED: {
			const value = action.payload;

			return {
				...state,
				searchValue: value,
				isSearchInProgress: !!value.length,
				hasSearchError: false,
				shouldRetryLoadMore: false,
				isLoadMoreInProgress: false,
				searchResults: {
					inLocation: emptyBoardsList(),
					notInLocation: emptyBoardsList(),
				},
			};
		}

		case SEARCH_SUCCESS: {
			const { inLocation, notInLocation } = action.payload;

			return {
				...state,
				isSearchInProgress: false,
				searchResults: {
					inLocation,
					notInLocation,
				},
			};
		}

		case SEARCH_FAILURE: {
			return {
				...state,
				isSearchInProgress: false,
				hasSearchError: true,
			};
		}

		case LOAD_MORE: {
			return {
				...state,
				hasSearchError: false,
				isLoadMoreInProgress: true,
			};
		}

		case LOAD_MORE_SUCCESS: {
			const { values, total } = action.payload;

			const isSearchUsed = !!state.searchValue;
			const sourceValues = isSearchUsed ? state.searchResults : state.initialBoards;
			const mergedValues = [...sourceValues.inLocation.values, ...values];
			const uniqueValues = uniqBy(mergedValues, 'id');

			const results = {
				...sourceValues,
				inLocation: {
					values: uniqueValues,
					total,
				},
			};

			const propName = isSearchUsed ? 'searchResults' : 'initialBoards';

			return {
				...state,
				isLoadMoreInProgress: false,
				shouldRetryLoadMore: false,
				[propName]: results,
			};
		}

		case LOAD_MORE_FAILURE: {
			return {
				...state,
				isLoadMoreInProgress: false,
				shouldRetryLoadMore: true,
			};
		}

		case FETCHED_BOARD_OWNER_ACCOUNT_ID: {
			return {
				...state,
				boardOwnerAccountId: action.payload,
			};
		}

		default:
			return state;
	}
};
